import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import "./ProjectFinance.css";
import { getAllProjectsSummary, getProjectById } from "../../../api/projectApi";
import { selectStyles } from "../../../utils/selectStyles";
import { UserContext } from "../../../contexts/CurrentUserContext";
import {
  getProjectEstimate,
  getCommercialProposal,
  getCpTeis,
} from "../../../api/projectEstimatesApi";
import InitialData from "./InitialData/InitialData";
import Estimate from "./Estimate/Estimate";
import CommercialOffer from "./CommercialOffer/CommercialOffer";
import FinancialPlan from "./FinancialPlan/FinancialPlan";
import Agreements from "./Agreements/Agreements";
import PlanFactAnalysis from "./Fact/PlanFactAnalysis";

const tabs = [
  { id: "tep", label: "ТЭП и исходные данные" },
  { id: "offer", label: "Коммерческое предложение" },
  { id: "estimate", label: "Смета на проект. работы" },
  { id: "plan", label: "Финансовый план" },
  { id: "agreements", label: "Соглашения на физ/юр лица" },
  { id: "fact", label: "Факт" },
  // { id: "bonus_pre", label: "Расчет бонусов до экспертизы" },
  // { id: "bonus_post", label: "Расчет бонусов после экспертизы" },
  // { id: "closure", label: "Закрытие проекта" },
  // { id: "evaluation", label: "Оценка" },
];

const ProjectFinance = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useContext(UserContext);

  // Определяем роли пользователя
  const isAdms = userData?.access_levels?.some(
    (level) => level.name === "АДМС",
  );
  const isAdmp = userData?.access_levels?.some(
    (level) => level.name === "АДМП",
  );
  const isGip = userData?.access_levels?.some((level) => level.name === "ГИП");
  const isRo = userData?.access_levels?.some((level) => level.name === "РО");
  const isFin = userData?.access_levels?.some((level) => level.name === "ФИН");

  // Проверяем специальные исключения для пользователей с полным доступом как у АДМС
  const isSpecialUser = [54, 62, 167, 11, 94, 96, 98, 99].includes(
    userData?.id,
  );
  const isSpecialGroup = userData?.departments?.some((dept) =>
    dept.groups?.some((group) => group.id === 28),
  );

  // Определяем доступные вкладки в зависимости от роли
  const getAvailableTabs = () => {
    // Специальные пользователи, ГИП и АДМС получают доступ ко всем вкладкам
    if (isSpecialUser || isSpecialGroup || isAdms || isGip) {
      return tabs;
    }
    if (isAdmp || isRo || isFin) {
      // Для АДМП, РО и ФИН анализ трудозатрат вынесен в отдельный раздел "Анализ трудозатрат"
      return [];
    }
    // Для остальных показываем все вкладки (как у АДМС)
    return tabs;
  };

  const availableTabs = getAvailableTabs();

  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Блокировка редактирования сметы, КП и ТЭП при любом активном согласовании (смета или КП)
  const [projectLockedByApproval, setProjectLockedByApproval] = useState(false);

  // Получаем параметр tab из URL
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get("tab");

    // Если есть параметр tab в URL и он доступен для пользователя
    if (tabParam && availableTabs.some((tab) => tab.id === tabParam)) {
      return tabParam;
    }

    // Проверяем, есть ли сохраненное состояние для этого проекта
    const savedTab = localStorage.getItem(
      `projectFinance_activeTab_${projectId}`,
    );

    // Если есть сохраненное состояние и оно доступно для пользователя
    if (savedTab && availableTabs.some((tab) => tab.id === savedTab)) {
      return savedTab;
    }

    // Для АДМС, ГИП и специальных пользователей возвращаем вкладку "ТЭП" по умолчанию, для остальных - первую доступную
    if (isAdms || isGip || isSpecialUser || isSpecialGroup) {
      return "tep";
    }
    return availableTabs[0]?.id || "tep";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const summaryData = await getAllProjectsSummary();
        if (Array.isArray(summaryData)) {
          setProjects(
            summaryData.map((p) => ({
              value: p.id,
              label: p.name || "Без названия",
            })),
          );
        }
      } catch (err) {
        setError("Не удалось загрузить список проектов");
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const projectData = await getProjectById(projectId);
        setCurrentProject(projectData);
      } catch (err) {
        setError(`Не удалось загрузить данные проекта с ID ${projectId}`);
        setCurrentProject(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId]);

  // Блокировка при согласовании: используем флаг editable из API сметы и КП (бэкенд ставит false при согласовании)
  useEffect(() => {
    const checkApprovalLock = async () => {
      if (!projectId) {
        setProjectLockedByApproval(false);
        return;
      }
      try {
        const [estimateData, commercialData, teisData] = await Promise.all([
          getProjectEstimate(parseInt(projectId)).catch(() => null),
          getCommercialProposal(projectId).catch(() => null),
          getCpTeis(projectId).catch(() => null),
        ]);

        // Смета: бэкенд возвращает editable: false, когда смета на согласовании
        const estimateLocked = estimateData?.editable === false;

        // КП: бэкенд возвращает editable: false, когда КП на согласовании
        const commercialLocked =
          commercialData != null && commercialData.editable === false;

        // Дополнительно: статусы из ТЭП (на случай если editable ещё не обновился)
        const cpRaw = teisData?.cp_approval_status ?? "";
        const peRaw = teisData?.pe_approval_status ?? "";
        const cp = String(cpRaw).toLowerCase().trim();
        const pe = String(peRaw).toLowerCase().trim();
        const unlocked = (s) =>
          s === "отменен инициатором" ||
          s === "не согласован" ||
          s === "отклонен" ||
          s === "rejected";
        const cpOrPeLocked = (cp && !unlocked(cp)) || (pe && !unlocked(pe));

        const locked = estimateLocked || commercialLocked || cpOrPeLocked;
        setProjectLockedByApproval(locked);
      } catch (e) {
        // 404 (нет сметы/КП) обрабатываются через .catch(() => null) в Promise.all
        console.warn("Ошибка при проверке блокировки согласования:", e);
        setProjectLockedByApproval(false);
      }
    };

    checkApprovalLock();
  }, [projectId]);

  // Обновляем активную вкладку при изменении доступных вкладок
  useEffect(() => {
    const newInitialTab = getInitialTab();
    if (newInitialTab !== activeTab) {
      setActiveTab(newInitialTab);
    }
  }, [availableTabs]);

  // Для АДМП, РО, ФИН нет вкладок в Финансах — редирект на «Анализ трудозатрат»
  useEffect(() => {
    if (availableTabs.length === 0 && projectId) {
      navigate(`/projects/labor-analysis/${projectId}`, { replace: true });
    }
  }, [availableTabs.length, projectId, navigate]);

  // Сохраняем состояние при изменении projectId
  useEffect(() => {
    if (projectId) {
      // Сохраняем состояние только если пользователь активно переключил вкладку
      // (не при первом входе на проект)
      if (
        activeTab !== "tep" ||
        localStorage.getItem(`projectFinance_activeTab_${projectId}`)
      ) {
        localStorage.setItem(
          `projectFinance_activeTab_${projectId}`,
          activeTab,
        );
      }
    }
  }, [activeTab, projectId]);

  const handleProjectChange = (selectedOption) => {
    // Сохраняем текущее состояние перед сменой проекта
    const currentState = {
      activeTab: activeTab,
      activeBottomTab:
        localStorage.getItem(`estimate_activeBottomTab_${projectId}`) || "work",
    };

    // Очищаем состояния предыдущего проекта
    if (projectId) {
      localStorage.removeItem(`projectFinance_activeTab_${projectId}`);
      localStorage.removeItem(`estimate_activeBottomTab_${projectId}`);
    }

    // Сохраняем состояние для нового проекта
    localStorage.setItem(
      `projectFinance_activeTab_${selectedOption.value}`,
      currentState.activeTab,
    );
    localStorage.setItem(
      `estimate_activeBottomTab_${selectedOption.value}`,
      currentState.activeBottomTab,
    );

    // Для АДМП, ГИП, РО и ФИН (но не специальных пользователей) всегда открываем вкладку "Факт"
    if (
      (isAdmp || isGip || isRo || isFin) &&
      !isSpecialUser &&
      !isSpecialGroup
    ) {
      navigate(`/projects/labor-analysis/${selectedOption.value}`);
    } else {
      navigate(`/projects/finance/${selectedOption.value}`);
    }
  };

  const selectedProjectForSelect =
    projects.find((p) => p.value === parseInt(projectId)) || null;

  return (
    <div className="finance-container">
      <div className="finance-header">
        <h1 className="finance-title">
          {activeTab === "tep" && "ТЭП и исходные данные"}
          {activeTab === "offer" && "Коммерческое предложение"}
          {activeTab === "estimate" && "Смета на проект. работы"}
          {activeTab === "plan" && "Финансовый план"}
          {activeTab === "agreements" && "Соглашения на физ/юр лица"}
          {activeTab === "fact" && "Факт"}
          {activeTab === "bonus_pre" && "Расчет бонусов до экспертизы"}
          {activeTab === "bonus_post" && "Расчет бонусов после экспертизы"}
          {activeTab === "closure" && "Закрытие проекта"}
          {activeTab === "evaluation" && "Оценка"}
        </h1>
        <div className="finance-project-selector">
          <Select
            styles={selectStyles}
            options={projects}
            value={selectedProjectForSelect}
            onChange={handleProjectChange}
            placeholder="Выберите проект..."
            noOptionsMessage={() => "Проекты не найдены"}
            isLoading={loading && projects.length === 0}
          />
        </div>
      </div>

      <div className="finance-tabs">
        {availableTabs.map((tab) => (
          <button
            key={tab.id}
            className={`finance-tab-btn ${
              activeTab === tab.id ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab(tab.id);
              // Сохраняем активную вкладку в localStorage
              localStorage.setItem(
                `projectFinance_activeTab_${projectId}`,
                tab.id,
              );
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="finance-content">
        {activeTab === "tep" && (
          <InitialData
            projectId={projectId}
            projectLockedByApproval={projectLockedByApproval}
          />
        )}
        {activeTab === "offer" && (
          <CommercialOffer
            projectId={projectId}
            projectLockedByApproval={projectLockedByApproval}
          />
        )}
        {activeTab === "estimate" && (
          <Estimate
            projectId={projectId}
            projectLockedByApproval={projectLockedByApproval}
          />
        )}
        {activeTab === "plan" && <FinancialPlan projectId={projectId} />}
        {activeTab === "agreements" && <Agreements projectId={projectId} />}
        {activeTab === "fact" && <PlanFactAnalysis projectId={projectId} />}
        {/* Here will be other tabs */}
      </div>
    </div>
  );
};

export default ProjectFinance;
